import {
    Company,
    RegistrationCompanyData,
    RegistrationUserData,
    RegistrationVehicleDataV2,
    TeamleaderCreationRequest,
    TeamleaderCreationResponse,
    TeamleaderSubscriptionResponse,
    TwikeySignFormResponse,
    UserCreationResponse,
    UserLoginResponse
} from "../../types";
import { Api } from "../../api";
import { TaskEither, tryCatch } from "fp-ts/TaskEither";
import { RegistrationContext } from "./registrationV2Slice";


export type RegistrationError =
    | { errorType: 'UserRegistrationError', details: any }
    | { errorType: 'UserLoginError', details: any }
    | { errorType: 'CompanyCreationError', details: any }
    | { errorType: 'CompanyDetailsError', details: any }
    | { errorType: 'VehiclesRegistrationError', details: any }
    | { errorType: 'HubspotPublishError', details: any }
    | { errorType: 'TeamleaderCreationError', details: any }
    | { errorType: 'TwikeySignFormRequestError', details: any }
    | { errorType: 'TeamleaderSubscriptionError', details: any }

const createUserV2 = async (user: RegistrationUserData, registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const userCreationResponse: UserCreationResponse = await Api.createUser(user);
    const createdUser = {
        ...userCreationResponse.user,
        password: user.password
    }
    return { ...registrationContext, token: userCreationResponse.token, createdUser: createdUser, step: registrationContext.step++ };
}

export const createUserTask = (user: RegistrationUserData, initialRegistrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => createUserV2(user, initialRegistrationContext),
        (error) => ({ errorType: 'UserRegistrationError', details: error } as RegistrationError)
    )

const loginNewUserV2 = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const userLoginResponse: UserLoginResponse = await Api.login(registrationContext.createdUser!.email, registrationContext.createdUser!.password!);
    return { ...registrationContext, token: userLoginResponse.token, user: userLoginResponse.user, step: registrationContext.step++ }
};

export const loginUserTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => loginNewUserV2(registrationContext),
        (error) => ({ errorType: 'UserLoginError', details: error } as RegistrationError)
    )

const createCompanyV2 = async (company: RegistrationCompanyData, registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    await Api.createCompany(registrationContext.token!, company);
    return { ...registrationContext, step: registrationContext.step++ }
}

export const createCompanyTask = (company: RegistrationCompanyData, registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => createCompanyV2(company, registrationContext),
        (error) => ({ errorType: 'CompanyCreationError', details: error } as RegistrationError)
    )

const syncUserAsHubspotCustomer = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    await Api.syncUserAsHubspotCustomer(registrationContext.token!, {
        registrationType: registrationContext.flowType,
        telematicProvider: registrationContext.telematicProviderType,
        otherTelematicProvider: registrationContext.otherTelematicProvider,
        numberOfCars: registrationContext.numberOfVehicles,
        sector: registrationContext.sector
    });
    return { ...registrationContext }
}

export const syncUserAsHubspotCustomerTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => syncUserAsHubspotCustomer(registrationContext),
        (error) => ({ errorType: 'HubspotPublishError', details: error } as RegistrationError)
    )

const getCompanyDetails = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const company: Company = await Api.getCompanyDetails(registrationContext.token!);
    return { ...registrationContext, company, step: registrationContext.step++ }
}

export const getCompanyTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => getCompanyDetails(registrationContext),
        (error) => ({ errorType: 'CompanyDetailsError', details: error } as RegistrationError)
    )

const createVehicles = async (vehicles: RegistrationVehicleDataV2[], registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    if (vehicles.length > 0) {
        const createdVehicles = await Api.registerOemVehicles(registrationContext.token!, vehicles);
    }
    return { ...registrationContext, step: registrationContext.step++ }
}

export const createVehiclesTask = (vehicles: RegistrationVehicleDataV2[], registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => createVehicles(vehicles, registrationContext),
        (error) => ({ errorType: 'CompanyDetailsError', details: error } as RegistrationError)
    )

export const buildTeamleaderCreationRequest = (registrationContext: RegistrationContext): TeamleaderCreationRequest => ({
    name: registrationContext.company?.name!,
    vatNumber: registrationContext.company?.vat!,
    emails: [
        {
            type: "primary",
            email: registrationContext.company?.email!
        }
    ],
    addresses: [
        {
            type: "primary",
            city: registrationContext.company?.address.city!,
            countryCode: registrationContext.company?.address.countryCode!,
            houseNumber: registrationContext.company?.address.number!,
            postalCode: registrationContext.company?.address.postalCode!,
            street: registrationContext.company?.address.street!
        }
    ]
})

const createTeamleaderCompany = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const request = buildTeamleaderCreationRequest(registrationContext);
    const creationResponse: TeamleaderCreationResponse = await Api.createTeamleaderCompany(registrationContext.token!, request);

    return { ...registrationContext, teamleaderId: creationResponse.id, step: registrationContext.step++ }
}

export const createTeamleaderTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => createTeamleaderCompany(registrationContext),
        (error) => ({ errorType: 'TeamleaderCreationError', details: error } as RegistrationError)
    )

const requestTwikeyFormUrl = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const twikeySignFormResponse: TwikeySignFormResponse = await Api.createTwikeyInvite(registrationContext.token!,
        registrationContext.registrationId,
        registrationContext.twikeyPaymentType!
    );
    return { ...registrationContext, twikeySignUrl: twikeySignFormResponse.url, step: registrationContext.step++ }
}

export const requestTwikeyFormUrlTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => requestTwikeyFormUrl(registrationContext),
        (error) => ({ errorType: 'TwikeySignFormRequestError', details: error } as RegistrationError)
    )

const createTeamleaderSubscription = async (registrationContext: RegistrationContext): Promise<RegistrationContext> => {
    const response: TeamleaderSubscriptionResponse | undefined = await Api.createTeamleaderSubscription(
        registrationContext.token!,
        registrationContext.flowType,
        registrationContext.numberOfVehicles,
        registrationContext.twikeyPaymentType!
    )
    return { ...registrationContext, teamleaderSubscriptionId: response?.id }
}

export const requestTeamleaderSubscriptionTask = (registrationContext: RegistrationContext): TaskEither<RegistrationError, RegistrationContext> =>
    tryCatch(
        () => createTeamleaderSubscription(registrationContext),
        (error) => ({ errorType: 'TeamleaderSubscriptionError', details: error })
    )
